import React, {useEffect, useState} from 'react';

import classNames from 'classnames';

import {Container} from '../../components/container';
import {Button} from '../../components/button';
import {breakPoints, debounce, DebouncedFunction, TBreak} from '../../components/helpers';
import {ILogoProps, Logo} from '../../components/logo';
import {INavigationProps, Navigation} from '../../components/navigation';
import {ISidemenuProps, IUserProps, Sidemenu} from '../../components';
import {Capacitor} from '@capacitor/core';

export interface IHeaderProps {
    logo: ILogoProps;
    navigation?: INavigationProps;
    sideMenu?: ISidemenuProps;
    sideMenuWithLimitedNavigation?: ISidemenuProps;
    user?: IUserProps;
}

const Header = (props: IHeaderProps): JSX.Element => {
    const [isHidden, setIsHidden] = useState<boolean>(false);
    const [atTop, setAtTop] = useState<boolean>(true);
    const [windowWidth, setWindowWidth] = useState<TBreak>('xs');
    let lastScrollTop: number = 0;
    let direction: 'up' | 'down' | null = null;

    const onResize: () => void = (): void => {
        if (window.matchMedia(`(min-width: ${breakPoints.xl}px)`).matches) {
            setWindowWidth('xl');
        } else if (window.matchMedia(`(min-width: ${breakPoints.lg}px)`).matches) {
            setWindowWidth('lg');
        } else if (window.matchMedia(`(min-width: ${breakPoints.md}px)`).matches) {
            setWindowWidth('md');
        } else if (window.matchMedia(`(min-width: ${breakPoints.sm}px)`).matches) {
            setWindowWidth('sm');
        } else {
            setWindowWidth('xs');
        }
    };

    const onScroll = (): void => {
        setScrollDirection();
        setAtTop((window.pageYOffset || document.documentElement.scrollTop) < 5);

        if ((window.pageYOffset || document.documentElement.scrollTop) > 5) {
            setIsHidden(!(direction === 'up'));
        } else {
            setIsHidden(false);
        }
    };

    const debouncedResizeHandler: DebouncedFunction<() => void> = debounce(onResize, 50);
    const debouncedScrollHandler: DebouncedFunction<() => void> = debounce(onScroll, 50);
    const [sidemenuOpen, setSidemenuOpen] = useState<boolean>(!!props.sideMenu?.defaultIsOpen);

    useEffect(() => {
        onResize();
        window.addEventListener('resize', debouncedResizeHandler);
        window.addEventListener('scroll', debouncedScrollHandler);

        return (): void => {
            window.removeEventListener('resize', debouncedResizeHandler);
            window.removeEventListener('scroll', debouncedScrollHandler);
            debouncedResizeHandler.clear();
            debouncedScrollHandler.clear();
        };
    }, []);

    const className: string = classNames(
        Capacitor.getPlatform(),
        'header',
        {
            'header--with-sidemenu': true,
            'is-hidden': isHidden,
            'at-top': atTop,
        },
    );

    const setScrollDirection = (): void => {
        const st: number = window.pageYOffset || document.documentElement.scrollTop;

        if (st > lastScrollTop){
            direction = 'down';
        } else {
            direction = 'up';
        }

        lastScrollTop = st <= 0 ? 0 : st;
    };

    const renderSidemenuToggle: (sidemenu: ISidemenuProps) => JSX.Element = (sidemenu: ISidemenuProps) => (
        <Button
            {...sidemenu.toggle}
            className="header__sidemenu-toggle"
            color="white"
            icon="menu"
            onClick={(): void => setSidemenuOpen(true)}
            style="square"
        />
    );

    const renderNavigation = (navigation: INavigationProps): JSX.Element => (
        <Navigation
            {...navigation}
            className="header__navigation"
            direction="auto"
        />
    );

    const desktopHeader = (): JSX.Element => (
        <header className={className}>
            {props.sideMenuWithLimitedNavigation &&
                <Sidemenu
                    {...props.sideMenuWithLimitedNavigation}
                    className="header__sidemenu"
                    isOpen={sidemenuOpen}
                    onChange={(o: boolean): void => setSidemenuOpen(o)}
                />
            }
            <Container className="header__container">
                <div className="header__inner">
                    <Logo {...props.logo} className="header__logo"/>
                    <div className="header__column">
                        {props.navigation && renderNavigation(props?.navigation)}
                    </div>
                    <div className="header__main">
                        {props.sideMenuWithLimitedNavigation && renderSidemenuToggle(props.sideMenuWithLimitedNavigation)}
                    </div>
                </div>
            </Container>
        </header>
    );

    const mobileHeader = (): JSX.Element => (
        <header className={className}>
            {props.sideMenu &&
                <Sidemenu
                    {...props.sideMenu}
                    className="header__sidemenu"
                    isOpen={sidemenuOpen}
                    onChange={(o: boolean): void => setSidemenuOpen(o)}
                />
            }
            <Container className="header__container">
                <div className="header__inner">
                    <Logo {...props.logo} className="header__logo"/>
                    {(['lg'].includes(windowWidth)) &&
                    <div className="header__main">
                        {props.sideMenu && renderSidemenuToggle(props.sideMenu)}
                    </div>}
                    {(['xs', 'sm', 'md'].includes(windowWidth)) &&
                    <div className="header__column">
                        {props.sideMenu && renderSidemenuToggle(props.sideMenu)}
                    </div>}
                </div>
            </Container>
        </header>
    );

    if (['xs', 'sm', 'md', 'lg'].includes(windowWidth)){
        return mobileHeader();
    } else {
        return desktopHeader();
    }
};

export default Header;
